import { concatBytes, fromUtf8, utf8 } from './bytes.ts';

/**
 * A small deterministic CBOR (RFC 8949 §4.2.1) codec for NetID's on-chain
 * records: unsigned integers, byte strings, text strings, arrays and maps with
 * text keys. Map keys are sorted by their encoded bytes, integers use the
 * shortest head, and the decoder refuses anything else (floats, tags,
 * indefinite lengths, duplicate or unsorted keys, trailing bytes), so one
 * value has exactly one encoding.
 */
export type CborValue = number | string | Uint8Array | CborValue[] | { [key: string]: CborValue };

export function cborEncode(value: CborValue): Uint8Array {
  if (typeof value === 'number') return head(0, value);
  if (typeof value === 'string') {
    const b = utf8(value);
    return concatBytes(head(3, b.length), b);
  }
  if (value instanceof Uint8Array) return concatBytes(head(2, value.length), value);
  if (Array.isArray(value)) return concatBytes(head(4, value.length), ...value.map(cborEncode));
  if (value && typeof value === 'object') {
    const entries = Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => ({ key: cborEncode(k), value: cborEncode(v) }))
      .sort((a, b) => compareBytes(a.key, b.key));
    return concatBytes(head(5, entries.length), ...entries.flatMap((e) => [e.key, e.value]));
  }
  throw new Error(`CBOR: unsupported value ${String(value)}`);
}

export function cborDecode(buf: Uint8Array): CborValue {
  let pos = 0;
  const readHead = (): { major: number; value: number } => {
    if (pos >= buf.length) throw new Error('CBOR: truncated');
    const byte = buf[pos++];
    const major = byte >> 5;
    const info = byte & 0x1f;
    if (info < 24) return { major, value: info };
    const size = info === 24 ? 1 : info === 25 ? 2 : info === 26 ? 4 : info === 27 ? 8 : 0;
    if (!size || pos + size > buf.length) throw new Error('CBOR: unsupported length');
    let value = 0;
    for (let i = 0; i < size; i++) value = value * 256 + buf[pos + i];
    pos += size;
    if (!Number.isSafeInteger(value)) throw new Error('CBOR: integer too large');
    const min = size === 1 ? 24 : size === 2 ? 0x100 : size === 4 ? 0x10000 : 0x100000000;
    if (value < min) throw new Error('CBOR: non-shortest integer');
    return { major, value };
  };
  const read = (depth: number): CborValue => {
    if (depth > 8) throw new Error('CBOR: nested too deeply');
    const { major, value } = readHead();
    switch (major) {
      case 0:
        return value;
      case 2:
      case 3: {
        if (pos + value > buf.length) throw new Error('CBOR: truncated');
        const bytes = buf.slice(pos, pos + value);
        pos += value;
        return major === 2 ? bytes : fromUtf8(bytes);
      }
      case 4: {
        const out: CborValue[] = [];
        for (let i = 0; i < value; i++) out.push(read(depth + 1));
        return out;
      }
      case 5: {
        const out: Record<string, CborValue> = {};
        let prev: Uint8Array | null = null;
        for (let i = 0; i < value; i++) {
          const start = pos;
          const key = read(depth + 1);
          if (typeof key !== 'string') throw new Error('CBOR: map key is not text');
          const keyBytes = buf.subarray(start, pos);
          if (prev && compareBytes(prev, keyBytes) >= 0) throw new Error('CBOR: map keys not in canonical order');
          prev = keyBytes;
          out[key] = read(depth + 1);
        }
        return out;
      }
      default:
        throw new Error(`CBOR: unsupported major type ${major}`);
    }
  };
  const v = read(0);
  if (pos !== buf.length) throw new Error('CBOR: trailing bytes');
  return v;
}

export function compareBytes(a: Uint8Array, b: Uint8Array): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

function head(major: number, value: number): Uint8Array {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('CBOR: negative or unsafe integer');
  if (value < 24) return Uint8Array.of((major << 5) | value);
  if (value < 0x100) return Uint8Array.of((major << 5) | 24, value);
  if (value < 0x10000) return Uint8Array.of((major << 5) | 25, value >> 8, value & 0xff);
  if (value < 0x100000000) {
    return Uint8Array.of((major << 5) | 26, (value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff);
  }
  const hi = Math.floor(value / 0x100000000);
  const lo = value >>> 0;
  return Uint8Array.of((major << 5) | 27, 0, 0, (hi >>> 8) & 0xff, hi & 0xff, (lo >>> 24) & 0xff, (lo >>> 16) & 0xff, (lo >>> 8) & 0xff, lo & 0xff);
}
